import React, { useState, useEffect, useRef } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isPast, startOfDay } from 'date-fns';
import { initialSeasonMonth, nextSeasonMonth, prevSeasonMonth, SEASON_HINT } from '../../utils/season';
import { de } from 'date-fns/locale';
import CalendarService, { CalendarSlotGroup } from '../../services/CalendarService';
import { CalendarSlot } from '../../types/calendar.types';
import { ChevronLeft, ChevronRight, Users, Clock, Euro } from 'lucide-react';
import { calculateTieredPrice, TourTypeWithPriceTiers } from '../../utils/pricingUtils';
import { API_BASE_URL } from '../../config/api.config';
import './CalendarView.css';

interface CalendarViewProps {
  onSelectSlot?: (slot: CalendarSlot) => void;
  onDateSelect?: (date: Date) => void;
  minParticipants?: number;
  maxParticipants?: number;
  selectedTourType?: string;
  isAdminBooking?: boolean; // Admin kann Kapazitätsgrenzen umgehen
  // T-141 B (v1.8.43): Start auf dem Tag einer Terminanfrage (Monat und Tag geöffnet); ohne Prop wie bisher
  initialDate?: Date;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  onSelectSlot,
  onDateSelect,
  minParticipants = 1,
  maxParticipants = 140,
  selectedTourType,
  isAdminBooking = false,
  initialDate
}) => {
  // T-104 (v1.8.25): Start im laufenden Saisonmonat, außerhalb der Saison im nächsten Mai
  // T-141 B (v1.8.43): mit initialDate im Monat der Terminanfrage
  const [currentMonth, setCurrentMonth] = useState(initialDate && !isNaN(initialDate.getTime()) ? startOfMonth(initialDate) : initialSeasonMonth());
  const anfrageTagGeoeffnet = useRef(false);
  const [scheduleGroups, setScheduleGroups] = useState<CalendarSlotGroup[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [daySchedules, setDaySchedules] = useState<CalendarSlotGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tourTypes, setTourTypes] = useState<TourTypeWithPriceTiers[]>([]);

  // Load tour types with price tiers on mount
  useEffect(() => {
    const loadTourTypes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/tour-types`);
        if (response.ok) {
          const data = await response.json();
          console.log('[CalendarView] Loaded tour types with price tiers:', data.length);
          setTourTypes(data);
        }
      } catch (error) {
        console.error('[CalendarView] Error loading tour types:', error);
      }
    };
    loadTourTypes();
  }, []);

  // Load grouped schedules for current month
  useEffect(() => {
    loadMonthSchedules();
  }, [currentMonth, selectedTourType]);

  // T-141 B (v1.8.43): Tag der Terminanfrage einmalig öffnen, sobald die Termine des Monats geladen sind
  useEffect(() => {
    if (!initialDate || anfrageTagGeoeffnet.current || scheduleGroups.length === 0) return;
    anfrageTagGeoeffnet.current = true;
    // Review v1.8.43 Befund 5: vergangenen Tag nicht öffnen (wie handleDayClick)
    if (isPast(startOfDay(initialDate)) && !isSameDay(initialDate, new Date())) return;
    setSelectedDate(initialDate);
    onDateSelect?.(initialDate);
  }, [initialDate, scheduleGroups]);

  // Load schedules for selected day
  useEffect(() => {
    if (selectedDate) {
      loadDaySchedules(selectedDate);
    }
  }, [selectedDate]);

  const loadMonthSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const data = await CalendarService.getGroupedSlots(start, end, selectedTourType);
      console.log('Loaded schedule groups for month:', format(currentMonth, 'MMMM yyyy'), 'Count:', data.length);
      console.log('First few schedules:', data.slice(0, 3));
      setScheduleGroups(data);
    } catch (err) {
      setError('Fehler beim Laden der Termine');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadDaySchedules = async (date: Date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      // Filter schedule groups for the selected date
      const filtered = scheduleGroups.filter(group => group.date === dateStr);
      setDaySchedules(filtered);
    } catch (err) {
      console.error('Error loading day schedules:', err);
      setDaySchedules([]);
    }
  };
  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });

    // Add empty cells for proper weekday alignment (Monday = 0, Sunday = 6)
    const firstDayOfWeek = (start.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
    const emptyDays = Array(firstDayOfWeek).fill(null);

    return [...emptyDays, ...days];
  };

  const getSchedulesForDay = (date: Date | null) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return scheduleGroups.filter(group => group.date === dateStr);
  };

  const getDayStatus = (date: Date | null) => {
    if (!date) return 'unavailable';

    // Check if date is in the past
    const today = startOfDay(new Date());
    const checkDate = startOfDay(date);
    if (isPast(checkDate) && !isSameDay(checkDate, today)) {
      return 'past';
    }

    const daySchedules = getSchedulesForDay(date);
    if (daySchedules.length === 0) return 'unavailable';

    // Use Schedule-based capacity (one schedule = one physical boat trip)
    const totalAvailable = daySchedules.reduce((sum, s) => sum + s.availableCapacity, 0);
    const totalCapacity = daySchedules.reduce((sum, s) => sum + s.totalCapacity, 0);
    const totalBookings = daySchedules.reduce((sum, s) => sum + s.currentBookings, 0);
    const minRequired = Math.max(...daySchedules.map(s => s.minParticipants));

    // Determine status based on Schedule capacity
    if (totalAvailable === 0) {
      return 'full';
    }

    if (totalBookings < minRequired) {
      return 'needsMore';
    }

    if (totalAvailable <= 5) {
      return 'limited';
    }

    // Check for flexible groups
    const hasFlexible = daySchedules.some(s => s.currentBookings > 0 && s.currentBookings < minRequired);
    if (hasFlexible) {
      return 'flexible';
    }

    return 'available';
  };

  // T-104 (v1.8.25): Saison Mai bis Oktober, Oktober -> Mai des Folgejahres, Mai -> Oktober des Vorjahres
  const handlePrevMonth = () => setCurrentMonth(prevSeasonMonth(currentMonth));
  const handleNextMonth = () => setCurrentMonth(nextSeasonMonth(currentMonth));

  const handleDayClick = (date: Date) => {
    const status = getDayStatus(date);
    if (status !== 'unavailable' && status !== 'full' && status !== 'past') {
      console.log('[CalendarView] Date selected:', format(date, 'yyyy-MM-dd'));
      setSelectedDate(date);
      if (onDateSelect) {
        console.log('[CalendarView] Calling onDateSelect callback');
        onDateSelect(date);
      }
    }
  };

  const handleSlotSelect = (slot: CalendarSlot) => {
    console.log('[CalendarView] handleSlotSelect called with slot:', slot);
    console.log('[CalendarView] slot.tourType:', slot.tourType);
    if (onSelectSlot) {
      console.log('[CalendarView] Calling onSelectSlot callback');
      onSelectSlot(slot);
    } else {
      console.warn('[CalendarView] onSelectSlot callback is NOT defined!');
    }
  };

  return (
    <div className="calendar-view-container">
      {/* Calendar Header */}
      <div className="calendar-view-header">
        <button
          onClick={handlePrevMonth}
          className="calendar-nav-button"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <h2 className="calendar-view-title" title={SEASON_HINT}>
          {format(currentMonth, 'MMMM yyyy', { locale: de })}
        </h2>

        <button
          onClick={handleNextMonth}
          className="calendar-nav-button"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Loading/Error States */}
      {loading && (
        <div className="calendar-loading">
          Termine werden geladen...
        </div>
      )}

      {error && (
        <div className="calendar-error">
          {error}
        </div>
      )}
      {/* Calendar Grid */}
      {!loading && (
        <div>
          {/* Weekday Headers */}
          <div className="calendar-weekdays">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="calendar-days-grid">
            {getDaysInMonth().map((date, idx) => {
              // Handle empty cells for weekday alignment
              if (!date) {
                return <div key={`empty-${idx}`} className="calendar-day-cell calendar-day-empty" />;
              }

              const status = getDayStatus(date);
              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const daySchedules = getSchedulesForDay(date);

              // Calculate Schedule-based capacity info for display
              // IMPORTANT: Each Schedule = one physical boat trip at a specific time
              const availableSchedules = daySchedules.filter(s =>
                s.isAvailable &&
                (isAdminBooking || s.availableCapacity >= minParticipants) // Admin kann auch bei wenig Kapazität buchen
              );

              // Count schedules that need more participants
              const schedulesNeedingMore = daySchedules.filter(s =>
                s.currentBookings < s.minParticipants
              );

              // Determine display text based on status
              let displayText = '';
              let detailLines: string[] = [];

              if (status === 'past') {
                displayText = 'Vergangen';
              } else if (status === 'full') {
                displayText = 'Ausgebucht';
              } else if (status === 'unavailable') {
                displayText = 'Keine Fahrten';
              } else if (daySchedules.length > 0) {
                // Show number of available time slots (trips)
                const availableCount = availableSchedules.length;
                const totalCount = daySchedules.length;

                if (availableCount === 0) {
                  displayText = 'Keine Plätze';
                } else if (availableCount === 1) {
                  displayText = '1 Fahrt verfügbar';
                  // Show time for single trip
                  const schedule = availableSchedules[0];
                  detailLines = [`${schedule.startTime.substring(0, 5)} Uhr`];
                } else {
                  displayText = `${availableCount} Fahrten verfügbar`;
                  // Show times for multiple trips
                  detailLines = availableSchedules
                    .map(s => `${s.startTime.substring(0, 5)} Uhr`)
                    .slice(0, 2); // Show max 2 times
                  if (availableCount > 2) {
                    detailLines.push(`+${availableCount - 2} weitere`);
                  }
                }
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleDayClick(date)}
                  disabled={status === 'unavailable' || status === 'full' || status === 'past'}
                  className={`
                    calendar-day-cell
                    calendar-day-${status}
                    ${isSelected ? 'calendar-day-selected' : ''}
                  `}
                >
                  <div className="calendar-day-number">{format(date, 'd')}</div>
                  {daySchedules.length > 0 && (
                    <div className="calendar-day-info">
                      <div className="calendar-day-status-text">{displayText}</div>
                      {detailLines.map((line, idx) => (
                        <div key={idx} className="calendar-day-detail">{line}</div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {/* Legend */}
      <div className="calendar-legend">
        <div className="calendar-legend-item">
          <div className="calendar-legend-color available"></div>
          <span>Verfügbar</span>
        </div>
        <div className="calendar-legend-item">
          <div className="calendar-legend-color needsMore"></div>
          <span>Weitere benötigt</span>
        </div>
        <div className="calendar-legend-item">
          <div className="calendar-legend-color flexible"></div>
          <span>Flexible Gruppe</span>
        </div>
        <div className="calendar-legend-item">
          <div className="calendar-legend-color limited"></div>
          <span>Wenige Plätze frei</span>
        </div>
        <div className="calendar-legend-item">
          <div className="calendar-legend-color full"></div>
          <span>Ausgebucht</span>
        </div>
        <div className="calendar-legend-item">
          <div className="calendar-legend-color past"></div>
          <span>Vergangen</span>
        </div>
      </div>

      {/* Selected Day Schedules - Grouped Display - AUSGEBLENDET */}
      {/* Dieser Bereich wurde auf Anfrage ausgeblendet - die Slot-Auswahl erfolgt direkt über den Kalender */}
      {false && selectedDate && daySchedules.length > 0 && (
        <div className="calendar-time-slots">
          <h3 className="calendar-time-slots-header">
            Verfügbare Termine am {format(selectedDate!, 'dd. MMMM yyyy', { locale: de })}
          </h3>

          <div className="calendar-time-slot-list">
            {daySchedules.map((schedule) => (
              <div key={schedule.scheduleId} className="calendar-schedule-group">
                {/* Schedule Header - Shows boat trip info */}
                <div className="calendar-schedule-header">
                  <div className="calendar-schedule-time">
                    <Clock className="h-5 w-5" style={{ color: '#6c757d' }} />
                    <span>{schedule.startTime} Uhr</span>
                  </div>
                  <div className="calendar-schedule-capacity">
                    <Users className="h-5 w-5" style={{ color: '#6c757d' }} />
                    <span>{schedule.availableCapacity} / {schedule.totalCapacity} Plätze verfügbar</span>
                  </div>
                  <div className="calendar-capacity-bar">
                    <div
                      className="calendar-capacity-fill"
                      style={{
                        width: `${(schedule.currentBookings / schedule.totalCapacity) * 100}%`,
                        backgroundColor: schedule.colorCode || '#4ade80'
                      }}
                    />
                  </div>
                </div>

                {/* Ticket Types (Slots) for this Schedule */}
                <div className="calendar-slot-types">
                  {schedule.slots.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => handleSlotSelect({
                        id: slot.id,
                        date: schedule.date,
                        time: schedule.startTime,
                        tourType: slot.tourType as 'STANDARD' | 'ALL_IN' | 'ALL_IN_PREMIUM' | 'EXCLUSIVE' | 'WEDDING',
                        pricePerPerson: slot.pricePerPerson,
                        maxCapacity: schedule.totalCapacity,
                        minCapacity: schedule.minParticipants,
                        currentBookings: slot.currentBookings,
                        confirmedBookingsCount: schedule.currentBookings,
                        pendingRequestsCount: 0,
                        availableCapacity: schedule.availableCapacity,
                        capacityPercentage: (schedule.currentBookings / schedule.totalCapacity) * 100,
                        isAvailable: slot.isAvailable,
                        isBlocked: slot.isBlocked,
                        isConfirmed: schedule.isConfirmed,
                        waitlistCount: 0,
                        specialNotes: slot.specialNotes,
                        colorCode: schedule.colorCode,
                        displayDate: format(new Date(schedule.date), 'dd.MM.yyyy'),
                        displayTime: schedule.startTime,
                        status: (schedule.isAvailable ? 'available' : slot.isBlocked ? 'blocked' : 'full') as 'pending' | 'available' | 'limited' | 'full' | 'blocked'
                      })}
                      disabled={!slot.isAvailable || slot.isBlocked || (!isAdminBooking && schedule.availableCapacity < minParticipants)}
                      className="calendar-slot-type-button"
                    >
                      <div className="calendar-slot-type-info">
                        <div className="calendar-slot-type-name">{slot.tourType}</div>
                        <div className="calendar-slot-type-price">
                          <Euro className="h-4 w-4" style={{ color: '#6c757d' }} />
                          <span>
                            {(() => {
                              // Berechne den Staffelpreis basierend auf der Teilnehmerzahl
                              const priceResult = calculateTieredPrice(
                                slot.tourType,
                                minParticipants,
                                tourTypes,
                                slot.pricePerPerson
                              );
                              // Festpreis vs. Pro Person unterscheiden
                              if (priceResult.pricingMode === 'FixedPrice') {
                                return `${priceResult.pricePerPerson.toFixed(2)} € Festpreis`;
                              }
                              return `${priceResult.pricePerPerson.toFixed(2)} € pro Person`;
                            })()}
                          </span>
                        </div>
                      </div>
                      {slot.specialNotes && (
                        <div className="calendar-slot-type-note">
                          {slot.specialNotes}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;